import React, { useEffect, useState } from 'react'
import { FbButton, Icons, LoadingIndicator, Table } from '@fabricks/ui-components'
import { FbTableColumn } from '@fabricks/ui-components/dist/components/Table/Table'
import { useKeycloak } from 'react-keycloak'
import moment from 'moment'
import {
    GetProcessLogsQuery,
    ProcessLogDto,
    ProcessLogMetaParamDto,
    useGetProcessLogsLazyQuery
} from "../../api/models";
import { createBaseContext } from "../../services/apiService";
import { get as _get } from "lodash"
import { showInfo, showSuccess } from "../../utils/sweetAlertHelper";
import Swal from "sweetalert2";
import axios from "axios";
import getEnvVars from "../../environment/environment";
import { downloadFile } from "../../utils/apiUtils";

interface ProcessOverviewProps {
    onShowResult?: (result: ProcessLogMetaParamDto[]) => void
    renderFinished?: () => React.ReactElement
    type: string
    onRowClick?: (uuid: string, row: any | undefined) => void
}

export const ProcessOverview: React.FC<ProcessOverviewProps> = (props: ProcessOverviewProps) => {
    const { renderFinished, onRowClick, type, onShowResult } = props
    const [keycloak] = useKeycloak()
    const [tableRows, setTableRows] = useState([])

    const [processLogsQuery, processLogsResponse] = useGetProcessLogsLazyQuery(
        createBaseContext(keycloak.token || '', {
            filter: [{ field: 'type', expected: type }],
            sort: [{ field: 'start', order: 'DESC' }]
        }))

    useEffect(() => {
        if (!processLogsResponse.called) {
            processLogsQuery()
        }
    }, [processLogsResponse, processLogsQuery])

    useEffect(() => {
        const interval = setInterval(() => {
            processLogsQuery()
        }, 5000)
        return () => clearInterval(interval)
    }, [processLogsQuery])

    useEffect(() => {
        const transformResponseToRows = () => {
            const queryData = processLogsResponse.data as GetProcessLogsQuery
            const extractedData = _get(queryData, "getProcessLogs.data", [])
            const rows = extractedData.map((logEntry: ProcessLogDto) => {
                return {
                    date: logEntry.start, uuid: logEntry.uuid,
                    name: logEntry.type, status: logEntry.status, progress: logEntry.status,
                    params: _get(logEntry.meta2, 'params', []),
                    result: _get(logEntry.meta2, 'result', []),
                    meta: logEntry.meta, download: logEntry,
                }
            })
            setTableRows(rows)
        }
        if (processLogsResponse.data && !processLogsResponse.loading) {
            transformResponseToRows()
        }
    }, [processLogsResponse.data])

    const renderDate = (value: any) => {
        return value ? moment(Number.parseInt(value, 10)).format("DD.MM.YYYY HH:mm:ss") : ""
    }


    const renderResult = (result: ProcessLogMetaParamDto[]) => {
        const onParamsClick = () => {
            if (onShowResult) {
                return onShowResult(result)
            }
            const html = result.map((param) => `<strong>${param.name}: </strong> ${param.value}`)
            showSuccess(`<div>${html.join('<br/>')}</div>`, 'Result')
        }

        if (result.length > 0) {
            return (
                <>
                    <Icons.Input onClick={onParamsClick} opacity={1} />
                </>)
        }
        return (<>
            <Icons.Input opacity={0.1} /></>)
    }

    const renderDownload = (logEntry: ProcessLogDto) => {
        const finished = logEntry.status === 'FINISHED'

        const handleDownload = (metaUuid: string) => () => {
            Swal.fire({
                title: `Download`,
                text: 'The optimization result is ready and can be downloaded',
                showLoaderOnConfirm: true,
                icon: 'success',
                confirmButtonText: 'Download',
                preConfirm: () => {
                    return axios
                        .get(`${getEnvVars.bff.host}/optimization/result/${metaUuid}?name=optimization-result`, {
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                                Authorization: keycloak.token ? `Bearer ${keycloak.token}` : '',
                            },
                            responseType: 'blob',
                        })
                        .then(downloadFile)
                },
            })
        }

        if (finished) {
            return (
                <>
                    <Icons.CloudDownload onClick={handleDownload(logEntry.uuid || '')} opacity={1} />
                </>)
        }
        return (<>
            <Icons.CloudDownload opacity={0.1} /></>)
    }

    const renderParams = (params: ProcessLogMetaParamDto[]) => {
        const onParamsClick = () => {
            const html = params.map((param) => `<strong>${param.name}: </strong> ${param.value}`)
            showInfo(`<div>${html.join('<br/>')}</div>`, 'Used parameters')
        }
        return (<Icons.Tune onClick={onParamsClick} />)
    }

    const renderProgress = (value: any) => {
        if (typeof value === 'string') {
            if (value.toUpperCase() === 'IN_PROGRESS') {
                return <LoadingIndicator styles={{ height: 10 }} variant="indeterminate" />
            }
            if (renderFinished) {
                return renderFinished()
            }
            return <LoadingIndicator value={100} styles={{ height: 10 }} variant="determinate" />
        }
    }

    const renderStatus = (value: any) => {
        switch (value as string) {
            case 'FINISHED': {
                return (<Icons.Check />)
            }
            case 'INIT': {
                return (<Icons.HourglassEmpty />)
            }
            case 'IN_PROGRESS': {
                return (<Icons.Autorenew />)
            }
            case 'ERROR': {
                return (<Icons.Error />)
            }
            default: {
                return (<span>{value}</span>)
            }
        }
    }

    const processOverviewColumns: FbTableColumn[] = [
        { id: 'date', label: 'Date', renderValue: renderDate },
        { id: 'params', label: 'Params', renderValue: renderParams },
        { id: 'status', label: 'State', renderValue: renderStatus },
        { id: 'result', label: 'Result', renderValue: renderResult },
        { id: 'download', label: 'Download', renderValue: renderDownload },
        { id: 'progress', label: 'Progress', minWidth: 150, renderValue: renderProgress },
    ]

    const prepareOnRowClick = (uuid: string) => {
        const filteredRow = tableRows.filter(row => _get(row, 'uuid', null) === uuid)
        if (filteredRow.length > 0 && onRowClick) {
            return onRowClick(uuid, filteredRow[0])
        }
    }


    return <Table columns={processOverviewColumns} rows={tableRows} onRowClick={prepareOnRowClick} />
}
